/**
 * The version upload (spec §3.3, §6, O14; plan R8): a results bundle and its model.pkl, of which only the sha256 and
 * the file name leave the machine, and a note. /admin/mmm-models and /budget's box 1 share it. The route decides who
 * may upload and checks the bundle, and its refusal is shown verbatim, one line each. The form asks for no role.
 * It refuses, before posting, only a bundle for another workspace: the route stores a bundle under its own
 * workspace_id, so that upload would land, active at once, on a workspace this page does not show.
 *
 * @module components/budget/VersionUploadForm
 */
import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { uploadVersion } from '@/services/budget/budgetAdminService';
import type { TFn } from '@/services/channels/channelFormat';

const INPUT_CLASS = 'block w-full text-sm text-ink-900 file:mr-3 file:rounded-md file:border-0 file:bg-neutral-100 '
  + 'file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-ink-900';
const BUTTON_CLASS = 'rounded-md bg-ink-950 px-3 py-1.5 text-sm font-medium text-white hover:bg-ink-900 disabled:opacity-50';

/** The route's note limit (api/_lib/mmmVersions.ts). */
const NOTE_MAX = 500;

type Outcome = { kind: 'stored'; version: number } | { kind: 'refused'; message: string };

/** The bundle's workspace_id when it names another workspace; null otherwise, and the route then judges the file. */
const otherWorkspaceOf = async (bundle: File, workspaceId: string): Promise<string | null> => {
  try {
    const id = (JSON.parse(await bundle.text()) as { workspace_id?: unknown } | null)?.workspace_id;
    return typeof id === 'string' && id !== workspaceId ? id : null;
  } catch {
    // Not JSON: uploadVersion reads it again and returns the parser's reason.
    return null;
  }
};

/** The guard, then the route: what the form shows next. */
const sendUpload = async (bundle: File, pkl: File, note: string, workspaceId: string, t: TFn): Promise<Outcome> => {
  const other = await otherWorkspaceOf(bundle, workspaceId);
  if (other !== null) return { kind: 'refused', message: t('budgetAdmin.upload.otherWorkspace', { id: other }) };
  const res = await uploadVersion(bundle, pkl, note);
  if (res.success && res.data) return { kind: 'stored', version: res.data.version };
  // T17b: the browser's own refusal is worded in the page's language; a route's stays verbatim.
  if (res.insecureContext) return { kind: 'refused', message: t('budgetAdmin.upload.insecureContext') };
  return { kind: 'refused', message: res.error ?? t('budgetAdmin.upload.failed') };
};

const FileField: React.FC<{ label: string; accept: string; hint?: string; onFile: (f: File | null) => void }> = (
  { label, accept, hint, onFile },
) => {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="mb-1 block font-medium text-neutral-800">{label}</label>
      <input id={id} type="file" accept={accept} className={INPUT_CLASS} onChange={(e) => onFile(e.target.files?.[0] ?? null)} />
      {hint !== undefined && <p className="mt-1 text-xs text-neutral-700">{hint}</p>}
    </div>
  );
};

const OutcomeLine: React.FC<{ outcome: Outcome }> = ({ outcome }) => {
  const { t } = useTranslation();
  if (outcome.kind === 'stored') {
    return <p role="status" className="font-medium text-success-700">{t('budgetAdmin.upload.stored', { version: outcome.version })}</p>;
  }
  return (
    <div role="alert" className="rounded-lg border border-error-700 px-3 py-2 text-error-700">
      <p className="font-semibold">{t('budgetAdmin.upload.failed')}</p>
      <p className="whitespace-pre-wrap break-words">{outcome.message}</p>
    </div>
  );
};

interface Props {
  workspaceId: string;
  /** After a 201, with the stored version's number: it is stored and active (plan R7), so the caller reads again. */
  onUploaded: (version: number) => void;
}

export const VersionUploadForm: React.FC<Props> = ({ workspaceId, onUploaded }) => {
  const { t } = useTranslation();
  const noteId = useId();
  const [bundle, setBundle] = useState<File | null>(null);
  const [pkl, setPkl] = useState<File | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  const submit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!bundle || !pkl) return;
    const form = e.currentTarget;
    setBusy(true);
    setOutcome(null);
    const result = await sendUpload(bundle, pkl, note, workspaceId, t);
    setBusy(false);
    setOutcome(result);
    if (result.kind !== 'stored') return;
    // Cleared, so a second press cannot store the same bundle again as the next version.
    form.reset();
    setBundle(null);
    setPkl(null);
    setNote('');
    onUploaded(result.version);
  };

  return (
    <form onSubmit={(e) => void submit(e)} className="space-y-3 text-sm">
      <FileField label={t('budgetAdmin.upload.bundle')} accept=".json,application/json" onFile={setBundle} />
      <FileField label={t('budgetAdmin.upload.pkl')} accept=".pkl" hint={t('budgetAdmin.upload.pklNote')} onFile={setPkl} />
      <div>
        <label htmlFor={noteId} className="mb-1 block font-medium text-neutral-800">{t('budgetAdmin.upload.note')}</label>
        <input id={noteId} type="text" maxLength={NOTE_MAX} value={note} onChange={(e) => setNote(e.target.value)}
          className="w-full rounded-lg border border-neutral-300 px-2 py-1 text-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-ink-950" />
      </div>
      <button type="submit" disabled={!bundle || !pkl || busy} className={BUTTON_CLASS}>
        {busy ? t('budgetAdmin.upload.uploading') : t('budgetAdmin.upload.submit')}
      </button>
      {outcome !== null && <OutcomeLine outcome={outcome} />}
    </form>
  );
};
